import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle, AlertCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface LabResult {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string;
  source_document_id: string;
  subcategory_id: string;
}

interface SubcategoryData {
  id: string;
  name: string;
  resultsByMonth: {
    [monthYear: string]: any[];
  };
  allResults: LabResult[];
}

interface AIInsight {
  type: "success" | "warning" | "info";
  title: string;
  message: string;
}

interface SubcategoryInsightsModalProps {
  subcategory: SubcategoryData;
  onClose: () => void;
}

export const SubcategoryInsightsModal = ({ subcategory, onClose }: SubcategoryInsightsModalProps) => {
  const [insights, setInsights] = useState<AIInsight[] | null>(null);
  const [loading, setLoading] = useState(true);

  // métricas locais para UI (cor em % fora da ref)
  const abnormalRatio = useMemo(() => {
    const rows = subcategory.allResults.filter(
      (r) => r.value != null && r.ref_low != null && r.ref_high != null
    );
    if (rows.length === 0) return null;
    const out = rows.filter(
      (r) => (r.value as number) < (r.ref_low as number) || (r.value as number) > (r.ref_high as number)
    );
    return Math.round((out.length / rows.length) * 100);
  }, [subcategory.allResults]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("generate-subcategory-insights", {
          body: {
            subcategoryName: subcategory.name,
            results: subcategory.allResults.map((r) => ({
              date: r.collected_at,
              value: r.value,
              unit: r.unit,
              testName: r.test_name,
              refLow: r.ref_low,
              refHigh: r.ref_high,
            })),
          },
        });
        if (error) throw error;
        setInsights((data?.insights ?? []) as AIInsight[]);
      } catch (err) {
        console.error("Erro ao gerar insights:", err);
        setInsights([
          {
            type: "info",
            title: "Análise local mínima",
            message: "Não foi possível consultar a IA agora. A avaliação mostrada considera apenas estatísticas locais.",
          },
        ]);
      } finally {
        setLoading(false);
      }
    })();
  }, [subcategory.id, subcategory.allResults]);

  const isOutOfRange = (result: LabResult): boolean => {
    if (result.value === null) return false;
    if (result.ref_low !== null && result.value < result.ref_low) return true;
    if (result.ref_high !== null && result.value > result.ref_high) return true;
    return false;
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Insights IA: {subcategory.name}</DialogTitle>
          <DialogDescription>Análise inteligente dos resultados ao longo do tempo</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Timeline de resultados */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-3">Histórico de Resultados</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {subcategory.allResults.map((result) => (
                  <div key={result.id} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {format(new Date(result.collected_at), "dd/MM/yyyy")}
                    </span>
                    <span
                      className={cn(
                        "font-medium",
                        isOutOfRange(result) && "text-red-600"
                      )}
                    >
                      {result.value !== null ? `${result.value} ${result.unit || ""}` : "—"}
                    </span>
                  </div>
                ))}
              </div>
              {abnormalRatio !== null && (
                <div className="mt-3 text-sm">
                  <span className="text-muted-foreground">Fora da referência: </span>
                  <span className={abnormalRatio >= 20 ? "text-red-600 font-semibold" : "text-emerald-600"}>
                    {abnormalRatio}%
                  </span>
                </div>
              )}
            </div>

            {/* Cards de insights */}
            {insights?.map((insight, idx) => (
              <Alert key={idx} variant={insight.type === "warning" ? "destructive" : "default"}>
                <AlertTitle className="flex items-center gap-2">
                  {insight.type === "success" && <CheckCircle className="h-4 w-4" />}
                  {insight.type === "warning" && <AlertCircle className="h-4 w-4" />}
                  {insight.type === "info" && <Info className="h-4 w-4" />}
                  {insight.title}
                </AlertTitle>
                <AlertDescription>{insight.message}</AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
